import { toValue } from 'vue'

import { useGlobal } from '@common/core-layer/composables/useGlobal'
import { BaseService } from '@common/http-layer/service/base.service'

import type {
  ActivityHeatmapResults,
  AnalyticsFilterBody,
  AnalyticsOverviewResults,
  AnalyticsResponse,
  AnalyticsTimeSeriesBody,
  AnalyticsCacheRefreshResponse,
  TimeseriesResults,
} from '../types/analytics.api'

/**
 * Community analytics API — `/community-analytics/{locationId}/groups/{groupId}/…`
 * GoKollab BFF stamps `trafficAppSource` / `appSource`; ClientClub sends `trafficAppSource: communities` on dashboard bodies.
 */
export class AnalyticsService extends BaseService {
  constructor() {
    super({
      baseURL: 'REST_API_URLS',
      basePath: '',
      serviceName: 'AnalyticsService',
    })
  }

  private getBasePath(locationId: string, groupId: string): string {
    const loc = locationId?.trim()
    const gid = groupId?.trim()
    if (!loc) throw new Error('Location ID is required')
    if (!gid) throw new Error('Group ID is required')
    return `/community-analytics/${loc}/groups/${gid}`
  }

  private isGokollabSurface(): boolean {
    return Boolean(toValue(useGlobal().isGokollab))
  }

  private withSurfaceFields<T extends AnalyticsFilterBody | AnalyticsTimeSeriesBody>(body: T, isGokollab: boolean): T {
    if (!isGokollab) {
      return { ...body, trafficAppSource: 'communities' }
    }
    const { trafficAppSource: _omit, ...rest } = body
    return rest as T
  }

  async getOverview(groupId: string, locationId: string, body: AnalyticsFilterBody): Promise<AnalyticsResponse<AnalyticsOverviewResults>> {
    const isGokollab = this.isGokollabSurface()
    const { data } = await this.post<AnalyticsResponse<AnalyticsOverviewResults>>(
      `${this.getBasePath(locationId, groupId)}/overview`,
      this.withSurfaceFields(body, isGokollab),
      { groupId, locationId }
    )
    return data
  }

  async getActivityHeatmap(
    groupId: string,
    locationId: string,
    body: AnalyticsFilterBody
  ): Promise<AnalyticsResponse<ActivityHeatmapResults>> {
    const isGokollab = this.isGokollabSurface()
    const { data } = await this.post<AnalyticsResponse<ActivityHeatmapResults>>(
      `${this.getBasePath(locationId, groupId)}/activity-heatmap`,
      this.withSurfaceFields(body, isGokollab),
      { groupId, locationId }
    )
    return data
  }

  async getTimeSeries(groupId: string, locationId: string, body: AnalyticsTimeSeriesBody): Promise<AnalyticsResponse<TimeseriesResults>> {
    const isGokollab = this.isGokollabSurface()
    const { data } = await this.post<AnalyticsResponse<TimeseriesResults>>(
      `${this.getBasePath(locationId, groupId)}/time-series`,
      this.withSurfaceFields(body, isGokollab),
      { groupId, locationId }
    )
    return data
  }

  async getInactiveMembersExport(groupId: string, locationId: string, body: AnalyticsFilterBody): Promise<Blob> {
    const { data } = await this.getBlob(`${this.getBasePath(locationId, groupId)}/export`, {
      params: {
        from: body.from,
        to: body.to,
        timezone: body.timezone,
      },
      groupId,
      locationId,
    })
    return data
  }

  async refreshCache(groupId: string, locationId: string): Promise<AnalyticsCacheRefreshResponse> {
    const { data } = await this.post<AnalyticsCacheRefreshResponse>(
      `${this.getBasePath(locationId, groupId)}/cache/refresh`,
      {},
      { groupId, locationId }
    )
    return data
  }
}
